import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

import type { ActiveFilterChip } from '../model/types';

interface ActiveFiltersRowProps {
  filters: ActiveFilterChip[];
  onClearAll?: () => void;
  className?: string;
}

/**
 * Presentational row of the currently applied filters, shown as removable
 * chips plus an optional "Clear all filters" action. Renders nothing when
 * there are no active filters.
 */
export function ActiveFiltersRow({ filters, onClearAll, className }: ActiveFiltersRowProps) {
  const { t } = useTranslation();

  if (filters.length === 0) {
    return null;
  }

  return (
    <div
      className={cn('flex flex-wrap items-center gap-2', className)}
      data-testid="active-filters-row"
      role="group"
      aria-label={t('cmrlist.applied.filters.label', 'Applied filters')}>
      {filters.map((filter) => (
        <Badge key={filter.key} variant="secondary" className="gap-1 rounded-lg py-1 pl-2.5 pr-1">
          <span className="truncate">{filter.label}</span>
          <button
            type="button"
            onClick={filter.onRemove}
            aria-label={`${t('common.removeFilter', 'Remove filter')}: ${filter.label}`}
            className="inline-flex size-4 items-center justify-center rounded-sm opacity-60 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <X className="size-3" />
          </button>
        </Badge>
      ))}

      {onClearAll && (
        <Badge
          asChild
          variant="ghost"
          className="h-auto cursor-pointer rounded-lg py-1 font-medium text-muted-foreground transition-colors hover:bg-neutral-alpha-200 hover:text-foreground active:bg-neutral-alpha-300">
          <button type="button" onClick={onClearAll}>
            {t('common.clearAllFilters', 'Clear all filters')}
            <X className="size-3" />
          </button>
        </Badge>
      )}
    </div>
  );
}
